import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

import { LocalisationService } from "services/LocalisationService";
import LocalisationEnGb from "data/en-GB";
import LocalisationEsEs from "data/es-ES";

export const DefaultLanguage = "en-GB";

const Languages = {
  "en-GB": LocalisationEnGb,
  "es-ES": LocalisationEsEs
};

/** Language tags the app can actually render, most preferred first. */
export const SupportedLanguages = Object.keys(Languages);

const QueryParamKey = "l";

export const isSupportedLanguage = language =>
  SupportedLanguages.includes(language);

/**
 * Matches on the primary subtag as well as the full tag, so a browser set to
 * any Spanish ("es", "es-AR", "es-419") resolves to es-ES rather than falling
 * through to English.
 */
const matchSupportedLanguage = tag => {
  if (!tag) return undefined;
  if (isSupportedLanguage(tag)) return tag;

  const primarySubtag = tag.split("-")[0].toLowerCase();
  return SupportedLanguages.find(
    supported => supported.split("-")[0].toLowerCase() === primarySubtag
  );
};

const requestedLanguage = () => {
  const queryParams = new URLSearchParams(window.location.search);
  return matchSupportedLanguage(queryParams.get(QueryParamKey));
};

/**
 * An explicit `?l=` is the only way to reach a non-default language, and the
 * choice deliberately does not outlive the page load.
 *
 * es-ES has unresolved layout problems — translated strings overrun their
 * containers in places — so it must never be selected on a visitor's behalf.
 * Detecting `navigator.language` would hand the broken layout to every
 * Spanish-locale visitor, and persisting the choice would strand anyone who
 * followed an `?l=es-ES` link with no way back now that the picker is not
 * mounted. Both were tried and removed for those reasons; restore them
 * together with the picker, not before.
 */
export const resolveInitialLanguage = () =>
  requestedLanguage() || DefaultLanguage;

const createService = language => {
  const service = new LocalisationService(DefaultLanguage, Languages[DefaultLanguage]);

  Object.entries(Languages)
    .filter(([name]) => name !== DefaultLanguage)
    .forEach(([name, textMap]) => service.addLanguage(name, textMap));

  service.setLanguage(language);
  return service;
};

export const LocalisationContext = createContext();
const LanguageContext = createContext();

export const LocalisationServiceConsumer = LocalisationContext.Consumer;

export const LocalisationServiceProvider = ({ children }) => {
  const [language, setLanguageState] = useState(resolveInitialLanguage);

  // A fresh service per language, because the context value has to change
  // identity for consumers to re-render. Mutating one shared instance via
  // setLanguage() leaves the value identical and the UI stays in the old
  // language — and this context is read both by hooks and by class components
  // through contextType, so there is no single hook to hang the update off.
  const localisationService = useMemo(() => createService(language), [language]);

  const setLanguage = useCallback(next => {
    if (!isSupportedLanguage(next)) return;
    setLanguageState(next);
  }, []);

  const languageControls = useMemo(
    () => ({ language, setLanguage, supportedLanguages: SupportedLanguages }),
    [language, setLanguage]
  );

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LocalisationContext.Provider value={localisationService}>
      <LanguageContext.Provider value={languageControls}>
        {children}
      </LanguageContext.Provider>
    </LocalisationContext.Provider>
  );
};

/** @return {LocalisationService} Retrieves the active localisation **/
export const useLocalisationService = () => useContext(LocalisationContext);
/** @return {string} Retrieves a translated string from the localisation service **/
export const useString = (key, substitutions) =>
  useLocalisationService().getString(key, substitutions);
/** @return {string | null} Retrieves a translated string from the localisation service, or `null` on failure. **/
export const useStringOrNull = (key, substitutions) =>
  useLocalisationService().getStringOrNull(key, substitutions);
/** @return {{language: string, setLanguage: function, supportedLanguages: string[]}} **/
export const useLanguage = () => useContext(LanguageContext);
